/* Filter the original published cohort; never edit or remove its records. */
(() => {
  'use strict';
  const PAGE_SIZE = 12;
  const RATINGS = ['all', 'critical', 'positive', 'star-1', 'star-2', 'star-3', 'star-4', 'star-5'];
  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  const ownedCards = section => {
    const grid = section.querySelector('[data-wi-review-grid]');
    if (!grid || grid.closest('[data-wi-reviews]') !== section) return [];
    return Array.from(grid.querySelectorAll('[data-wi-review]')).filter(card => card.closest('[data-wi-reviews]') === section && card.closest('[data-wi-review-grid]') === grid);
  };
  const uniqueCards = cards => {
    const result = new Map();
    cards.forEach(card => {
      const id = String(card.dataset.reviewId || '');
      if (id) result.set(id, result.has(id) ? null : card);
    });
    return result;
  };
  const boundCard = (item, section, byId) => {
    const id = String(item.dataset.wiPhotoReviewId || '');
    return item.closest('[data-wi-reviews]') === section ? byId.get(id) || null : null;
  };

  // A display derivative can fail independently of its full original.
  const recoverThumbnail = thumbnail => {
    const original = thumbnail?.dataset.wiThumbnailOriginal;
    if (!original || !thumbnail.hasAttribute('srcset')) return false;
    thumbnail.removeAttribute('srcset');
    thumbnail.src = original;
    return true;
  };
  function initialisePreviewThumbnails(scope) {
    scope.querySelectorAll('.wi-buyer-proof__image[data-wi-thumbnail-original]').forEach(thumbnail => {
      if (thumbnail.dataset.wiThumbnailReady) return;
      thumbnail.dataset.wiThumbnailReady = 'true';
      thumbnail.addEventListener('error', () => recoverThumbnail(thumbnail));
      if (thumbnail.complete && thumbnail.naturalWidth === 0) recoverThumbnail(thumbnail);
    });
  }

  function initialiseReviewInvitations(scope) {
    scope.querySelectorAll('[data-wi-reviews]').forEach(section => {
      if (section.dataset.wiReviewInvitationsReady) return;
      const productId = /^WorthItReviews-(\d+)$/.exec(section.id)?.[1];
      const details = section.querySelector('.wi-review-submit');
      if (!productId || !details || details.tagName !== 'DETAILS' || details.closest('[data-wi-reviews]') !== section || details.id !== `WorthItReviewDetails-${productId}`) return;
      const summary = details.querySelector('summary');
      if (!summary || summary.parentElement !== details) return;
      const photoHelp = details.querySelector('.wi-review-form__photo-wrap');
      section.querySelectorAll('[data-wi-review-form-open],[data-wi-review-photo-help-open]').forEach(link => {
        const photos = link.hasAttribute('data-wi-review-photo-help-open');
        const target = photos ? photoHelp : details;
        const expectedId = photos ? `WorthItReviewPhotosHelp-${productId}` : details.id;
        if (link.closest('[data-wi-reviews]') !== section || !target || target.id !== expectedId || link.getAttribute('href') !== `#${expectedId}` || link.getAttribute('aria-controls') !== expectedId) return;
        link.addEventListener('click', event => {
          if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          if (!section.isConnected || section.id !== `WorthItReviews-${productId}` || !details.isConnected || !target.isConnected || section.querySelector('.wi-review-submit') !== details || details.id !== `WorthItReviewDetails-${productId}` || details.closest('[data-wi-reviews]') !== section || link.closest('[data-wi-reviews]') !== section || summary.parentElement !== details || link.getAttribute('href') !== `#${expectedId}` || link.getAttribute('aria-controls') !== expectedId || (photos && details.querySelector('.wi-review-form__photo-wrap') !== photoHelp)) return;
          event.preventDefault();
          details.open = true;
          const focusTarget = photos ? photoHelp.querySelector('a[href^="mailto:"]') || summary : summary;
          focusTarget.focus({preventScroll: true});
          target.scrollIntoView({behavior: 'auto', block: 'start'});
        });
      });
      section.dataset.wiReviewInvitationsReady = 'true';
    });
  }

  function initialiseReviews(scope) {
    scope.querySelectorAll('[data-wi-reviews]').forEach(section => {
      if (section.dataset.wiReviewsReady) return;
      const grid = section.querySelector('[data-wi-review-grid]');
      const sort = section.querySelector('[data-wi-review-sort]');
      const filter = section.querySelector('[data-wi-review-filter]');
      if (!grid || !sort || !filter) return;
      const cards = ownedCards(section);
      const byId = uniqueCards(cards);
      const photoCards = new Set();
      section.querySelectorAll('[data-wi-photo-open]').forEach(item => {
        const card = boundCard(item, section, byId);
        if (card) photoCards.add(card);
      });
      const tools = section.querySelector('[data-wi-review-tools]');
      const lens = section.querySelector('[data-wi-review-lens]');
      const filterStatus = section.querySelector('[data-wi-review-filter-status]');
      const empty = section.querySelector('[data-wi-review-filter-empty]');
      const more = section.querySelector('[data-wi-review-more]');
      const photoFilter = section.querySelector('[data-wi-review-photo-filter]');
      const photoCount = section.querySelector('[data-wi-review-photo-count]');
      const clear = section.querySelector('[data-wi-review-clear]');
      if (tools && cards.length > 1) tools.hidden = false;
      if (lens && cards.length) lens.hidden = false;
      if (photoCount) photoCount.textContent = `(${photoCards.size})`;
      if (photoFilter) photoFilter.setAttribute('aria-label', `With buyer photos (${photoCards.size}) — filter published feedback`);
      section.dataset.wiPhotoReviewCount = String(photoCards.size);
      let withPhotos = false;
      let limit = PAGE_SIZE;
      let matching = [];
      let ordered = null;
      let orderKey = null;
      const compare = (a, b) => {
        const dates = String(b.dataset.date || '').localeCompare(String(a.dataset.date || ''));
        if (sort.value === 'lowest') return Number(a.dataset.rating) - Number(b.dataset.rating) || dates;
        if (sort.value === 'highest') return Number(b.dataset.rating) - Number(a.dataset.rating) || dates;
        return dates;
      };
      const apply = () => {
        if (ordered === null || orderKey !== sort.value) {
          ordered = [...cards].sort(compare);
          orderKey = sort.value;
          if (ordered.some((card, index) => grid.children[index] !== card)) {
            const batch = document.createDocumentFragment();
            ordered.forEach(card => batch.appendChild(card));
            grid.appendChild(batch);
          }
        }
        const selected = RATINGS.includes(filter.value) ? filter.value : 'all';
        if (filter.value !== selected) filter.value = selected;
        const exact = selected.startsWith('star-') ? Number(selected.slice(5)) : null;
        matching = ordered.filter(card => {
          const rating = Number(card.dataset.rating);
          const ratingMatch = exact !== null ? rating === exact : selected === 'critical' ? rating <= 3 : selected === 'positive' ? rating >= 4 : true;
          return ratingMatch && (!withPhotos || photoCards.has(card));
        });
        const visible = new Set(matching.slice(0, limit));
        cards.forEach(card => {
          const hidden = !visible.has(card);
          if (card.hidden !== hidden) card.hidden = hidden;
        });
        const displayed = Math.min(limit, matching.length);
        if (filterStatus) filterStatus.textContent = `${displayed} of ${matching.length} matching feedback ${matching.length === 1 ? 'entry' : 'entries'} shown. ${cards.length} published in total.${withPhotos ? ' Only entries with bound original buyer photos.' : ''}`;
        if (photoFilter) photoFilter.setAttribute('aria-pressed', String(withPhotos));
        if (clear) clear.hidden = selected === 'all' && !withPhotos;
        if (empty) {
          empty.hidden = matching.length > 0;
          empty.textContent = withPhotos ? 'No published feedback matches this rating and buyer-photo selection. Use Clear filters to see all published feedback.' : 'No published feedback matches this rating. Choose another rating or use Clear filters.';
        }
        if (more) {
          more.hidden = displayed >= matching.length;
          const additional = Math.min(PAGE_SIZE, matching.length - displayed);
          more.textContent = `Show ${additional} more ${additional === 1 ? 'entry' : 'entries'}`;
        }
        section.dispatchEvent(new CustomEvent('wi:reviews:updated', {detail: {rating: selected, withPhotos, matched: matching.length, displayed, total: cards.length}}));
      };
      filter.addEventListener('change', () => {limit = PAGE_SIZE; apply();});
      photoFilter?.addEventListener('click', () => {withPhotos = !withPhotos; limit = PAGE_SIZE; apply();});
      clear?.addEventListener('click', () => {
        const wasFocused = document.activeElement === clear;
        filter.value = 'all'; withPhotos = false; limit = PAGE_SIZE; apply();
        if (wasFocused) filter.focus({preventScroll: true});
      });
      sort.addEventListener('change', () => {
        limit = PAGE_SIZE; apply();
        const status = section.querySelector('[data-wi-review-status]');
        if (status) status.textContent = `Matching feedback sorted: ${sort.selectedOptions[0]?.textContent || 'Most recent'}.`;
      });
      more?.addEventListener('click', () => {
        const firstNew = matching[limit];
        limit += PAGE_SIZE; apply();
        if (more.hidden && firstNew) {firstNew.setAttribute('tabindex', '-1'); firstNew.focus({preventScroll: true});}
      });
      section.addEventListener('wi:review:reveal', event => {
        const card = byId.get(String(event.detail?.reviewId || ''));
        if (!card) return;
        filter.value = 'all'; withPhotos = false; apply();
        const index = ordered.indexOf(card);
        limit = Math.max(PAGE_SIZE, Math.ceil((index + 1) / PAGE_SIZE) * PAGE_SIZE);
        apply();
        card.setAttribute('tabindex', '-1');
        card.scrollIntoView({block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth'});
        card.focus({preventScroll: true});
      });
      // Only the cart-return controller supplies this temporary display state.
      section.addEventListener('wi:reviews:restore-ui', event => {
        const state = event.detail?.state;
        const productId = /^WorthItReviews-([1-9][0-9]{0,19})$/.exec(section.id)?.[1];
        const product = section.closest('product-info[id^="MainProduct-"]');
        if (event.target !== section || !section.isConnected || section.dataset.wiReviewsReady !== 'true' || !productId || product?.dataset.productId !== productId || product.dataset.url !== window.location.pathname.replace(/\/$/, '')) return;
        if (!state || Array.isArray(state) || typeof state !== 'object' || Object.keys(state).sort().join(',') !== 'photos,productId,rating,sort,visible') return;
        if (state.productId !== productId || !RATINGS.includes(state.rating) || !['newest', 'lowest', 'highest'].includes(state.sort) || typeof state.photos !== 'boolean' || !Number.isSafeInteger(state.visible) || state.visible < 0 || state.visible > 1200) return;
        if ([grid, filter, sort].some(node => node.closest('[data-wi-reviews]') !== section) || (state.photos && (!photoFilter || photoFilter.closest('[data-wi-reviews]') !== section))) return;
        filter.value = state.rating;
        sort.value = state.sort;
        withPhotos = state.photos;
        limit = Math.max(PAGE_SIZE, Math.min(Math.ceil(cards.length / PAGE_SIZE) * PAGE_SIZE, Math.ceil(Math.max(PAGE_SIZE, state.visible) / PAGE_SIZE) * PAGE_SIZE));
        apply();
        event.detail.accepted = true;
      });
      apply();
      section.dataset.wiReviewsReady = 'true';
      section.dispatchEvent(new CustomEvent('wi:reviews:ready', { bubbles: true }));
    });
  }

  function initialiseBuyerPhotos(scope) {
    scope.querySelectorAll('[data-wi-review-photos]').forEach(gallery => {
      if (gallery.dataset.wiPhotosReady) return;
      const rail = gallery.querySelector('[data-wi-photo-rail]');
      const items = Array.from(gallery.querySelectorAll('[data-wi-photo-open]'));
      if (!rail || !items.length) return;
      const isSourcePhoto = item => gallery.dataset.wiPhotoSourceProduct === 'WIU2026-17' && /^tiktok-photo-1732524467239489966-(?:o-20260830|l-20260827|clover-20260826)$/.test(item.dataset.wiPhotoSourceReviewId || '');
      const hasOnlySourcePhotos = items.every(isSourcePhoto);
      gallery.dataset.wiPhotosReady = 'true';
      const previous = gallery.querySelector('[data-wi-photo-previous]');
      const next = gallery.querySelector('[data-wi-photo-next]');
      const controls = gallery.querySelector('[data-wi-photo-controls]');
      const section = gallery.closest('[data-wi-reviews]');
      const byId = section ? uniqueCards(ownedCards(section)) : new Map();
      const updateRail = () => {
        const overflow = rail.scrollWidth - rail.clientWidth > 2;
        if (controls) controls.hidden = !overflow;
        if (previous) previous.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
      };
      const scrollRail = direction => {
        const step = (items[0].closest('[role="listitem"]')?.getBoundingClientRect().width || 240) + 14;
        rail.scrollBy({left: direction * step, behavior: reducedMotion() ? 'auto' : 'smooth'});
        const status = gallery.querySelector('[data-wi-photo-rail-status]');
        if (status) status.textContent = `Showing ${direction > 0 ? 'next' : 'previous'} ${hasOnlySourcePhotos ? 'source' : 'original'} buyer photos.`;
      };
      previous?.addEventListener('click', () => scrollRail(-1));
      next?.addEventListener('click', () => scrollRail(1));
      rail.addEventListener('scroll', updateRail, {passive: true});
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(updateRail); observer.observe(rail);
        document.addEventListener('shopify:section:unload', event => {if (event.target.contains(gallery)) observer.disconnect();});
      } else window.addEventListener('resize', updateRail, {passive: true});
      updateRail();
      const dialog = gallery.querySelector('[data-wi-photo-dialog]');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      const large = dialog.querySelector('[data-wi-photo-large]');
      const caption = dialog.querySelector('[data-wi-photo-caption]');
      const source = dialog.querySelector('[data-wi-photo-source]');
      const counter = dialog.querySelector('[data-wi-photo-counter]');
      const before = dialog.querySelector('[data-wi-photo-dialog-previous]');
      const after = dialog.querySelector('[data-wi-photo-dialog-next]');
      const close = dialog.querySelector('[data-wi-photo-close]');
      const failure = dialog.querySelector('[data-wi-photo-error]');
      const context = dialog.querySelector('[data-wi-photo-review-context]');
      const reviewHeading = dialog.querySelector('[data-wi-photo-review-heading]');
      const reviewText = dialog.querySelector('[data-wi-photo-review-text]');
      const reviewTitle = dialog.querySelector('[data-wi-photo-review-title]');
      const reviewByline = dialog.querySelector('[data-wi-photo-review-byline]');
      const reveal = dialog.querySelector('[data-wi-photo-read-review]');
      if (!large || !caption || !source || !counter || !before || !after || !close) return;
      let current = 0;
      let trigger = null;
      let revealOnClose = null;
      const showPhoto = index => {
        current = Math.max(0, Math.min(index, items.length - 1));
        const item = items[current];
        const thumbnail = item.querySelector('img');
        if (failure) failure.hidden = true;
        large.hidden = false;
        large.alt = thumbnail?.alt || (isSourcePhoto(item) ? 'Source buyer photo from the linked source listing' : 'Original buyer photo from the linked source listing');
        const imageWidth = Number(thumbnail?.getAttribute('width'));
        const imageHeight = Number(thumbnail?.getAttribute('height'));
        if (Number.isSafeInteger(imageWidth) && imageWidth > 0 && Number.isSafeInteger(imageHeight) && imageHeight > 0) {
          large.width = imageWidth; large.height = imageHeight;
        } else {
          large.removeAttribute('width'); large.removeAttribute('height');
        }
        large.src = item.href;
        caption.textContent = Array.from(item.querySelectorAll('figcaption > span:not(.visually-hidden)')).map(part => part.textContent.trim()).join(' · ');
        const sourceUrl = item.dataset.sourceUrl || '';
        if (/^https?:\/\//.test(sourceUrl)) {source.href = sourceUrl; source.hidden = false;}
        else {source.removeAttribute('href'); source.hidden = true;}
        counter.textContent = `${current + 1} of ${items.length} ${isSourcePhoto(item) ? 'source' : 'original'} buyer photos`;
        before.disabled = current === 0;
        after.disabled = current === items.length - 1;
        const card = section ? boundCard(item, section, byId) : null;
        const sourcePhotoContext = !card && isSourcePhoto(item) ? item.querySelector('[data-wi-photo-source-review-context]') : null;
        const sourcePhotoText = sourcePhotoContext?.querySelector('[data-wi-photo-source-review-text]')?.textContent.trim() || '';
        const sourcePhotoByline = sourcePhotoContext?.querySelector('[data-wi-photo-source-review-byline]')?.textContent.trim() || '';
        const hasSourcePhotoContext = Boolean(sourcePhotoText && sourcePhotoByline);
        if (context) context.hidden = false;
        if (reviewHeading) reviewHeading.textContent = card ? 'From the matching source review' : hasSourcePhotoContext ? 'From the matching source photo review' : 'Photo source';
        if (reviewTitle) {
          const title = card?.querySelector('.wi-review-card__title')?.textContent || '';
          reviewTitle.textContent = title; reviewTitle.hidden = !title;
        }
        if (reviewText) reviewText.textContent = card ? card.querySelector('blockquote')?.textContent || 'No written comment is published for this feedback entry.' : hasSourcePhotoContext ? sourcePhotoText : `${isSourcePhoto(item) ? 'This source photo' : 'This original photo'} is linked to its source listing; a matching published review has not been confirmed here.`;
        if (reviewByline) {
          const byline = card?.querySelector('.wi-review-card__byline');
          const parts = [byline?.querySelector('span')?.textContent.trim(), byline?.querySelector('time')?.textContent.trim()].filter(Boolean);
          reviewByline.textContent = card ? `${card.dataset.rating}/5 · ${parts.join(' · ')}` : hasSourcePhotoContext ? sourcePhotoByline : 'Source listing photo — review relationship unconfirmed';
        }
        if (reveal) reveal.hidden = !card;
      };
      large.addEventListener('error', () => {
        if (!large.getAttribute('src') || !large.complete || large.naturalWidth > 0) return;
        large.hidden = true;
        if (failure) failure.hidden = false;
      });
      large.addEventListener('load', () => {if (!large.complete || !large.naturalWidth) return; large.hidden = false; if (failure) failure.hidden = true;});
      const openPhoto = (index, origin) => {
        if (!gallery.isConnected || !dialog.isConnected || dialog.open || !Number.isSafeInteger(index) || index < 0 || index >= items.length || !items[index].isConnected) return false;
        trigger = origin; revealOnClose = null; showPhoto(index);
        try {dialog.showModal();} catch {trigger = null; return false;}
        close.focus();
        return true;
      };
      items.forEach((item, index) => {
        const hint = item.querySelector('.visually-hidden');
        if (hint) hint.textContent = isSourcePhoto(item) ? 'Open available source buyer photo' : 'Open larger original buyer photo';
        const thumbnail = item.querySelector('img');
        const handleThumbnailFailure = () => {
          if (recoverThumbnail(thumbnail)) return;
          item.dataset.wiPhotoLoadError = 'true';
          item.setAttribute('aria-label', isSourcePhoto(item) ? 'Source buyer photo could not load. Open the available source image or view its source.' : 'Original buyer photo could not load. Open the original image or view its source.');
        };
        thumbnail?.addEventListener('error', handleThumbnailFailure);
        if (thumbnail?.complete && thumbnail.naturalWidth === 0) handleThumbnailFailure();
        item.addEventListener('click', event => {
          if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
          if (openPhoto(index, item)) event.preventDefault();
        });
      });
      scope.querySelectorAll('[data-wi-buyer-preview]').forEach(link => {
        const index = Number(link.dataset.wiPhotoIndex);
        const item = items[index];
        if (link.dataset.wiBuyerPreviewReady || link.dataset.wiPhotoGalleryId !== gallery.id || !Number.isSafeInteger(index) || index < 0 || !item?.id || link.getAttribute('href') !== `#${item.id}`) return;
        link.addEventListener('click', event => {
          if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
          if (!link.isConnected || link.dataset.wiPhotoGalleryId !== gallery.id || Number(link.dataset.wiPhotoIndex) !== index || link.getAttribute('href') !== `#${item.id}` || gallery.querySelectorAll('[data-wi-photo-open]')[index] !== item) return;
          if (openPhoto(index, link)) event.preventDefault();
        });
        link.dataset.wiBuyerPreviewReady = 'true';
      });
      close.addEventListener('click', () => dialog.close());
      const movePhoto = direction => {
        const index = Math.max(0, Math.min(current + direction, items.length - 1));
        if (index === current) return;
        showPhoto(index);
        if (document.activeElement === before && before.disabled) (after.disabled ? close : after).focus({preventScroll: true});
        if (document.activeElement === after && after.disabled) (before.disabled ? close : before).focus({preventScroll: true});
      };
      before.addEventListener('click', () => {if (!before.disabled) movePhoto(-1);});
      after.addEventListener('click', () => {if (!after.disabled) movePhoto(1);});
      reveal?.addEventListener('click', () => {
        const card = section ? boundCard(items[current], section, byId) : null;
        if (!card) return;
        revealOnClose = String(card.dataset.reviewId); dialog.close();
      });
      dialog.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.target?.closest('input,textarea,select')) return;
        if (event.key === 'ArrowLeft') {event.preventDefault(); movePhoto(-1);}
        if (event.key === 'ArrowRight') {event.preventDefault(); movePhoto(1);}
      });
      dialog.addEventListener('click', event => {
        const rect = dialog.getBoundingClientRect();
        if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) dialog.close();
      });
      dialog.addEventListener('close', () => {
        large.removeAttribute('src'); large.hidden = false; if (failure) failure.hidden = true;
        const requested = revealOnClose; revealOnClose = null;
        if (requested && section) section.dispatchEvent(new CustomEvent('wi:review:reveal', {detail: {reviewId: requested}}));
        else if (trigger?.isConnected) trigger.focus({preventScroll: true});
        trigger = null;
      });
    });
  }
  const initialise = scope => {initialisePreviewThumbnails(scope); initialiseReviewInvitations(scope); initialiseReviews(scope); initialiseBuyerPhotos(scope);};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise(document));
  else initialise(document);
  document.addEventListener('shopify:section:load', event => initialise(event.target));
})();
